import { unwrapConnection } from 'src/app/shared/rxjs.pipes';
import { Component, Input, OnInit } from '@angular/core';
import { LoaderService } from 'src/app/shared/modules/loader/loader.service';
import { ContactDataSource } from 'src/app/shared/services/contact/contact.datasource';
import { AuthService, ContactRoleService, ContactService } from 'src/app/shared/services';
import {
  IApiCompany, IApiContact, IApiContactConnection, IApiContactFilterType, IApiContactRole, IApiContactRoleFilterType
} from 'src/app/shared/modules/graphql/types/types';
import { CreateUpdateContactComponent } from '../create-update-contact/create-update-contact.component';
import {
  contactsProfileContactInformationViewAssigned,
  contactsProfileCompanyInformationView,
  contactsInvestigations,
  contactsCreateConfig,
  contactsDelete,
} from "src/app/shared/helpers/auth-config/contacts.config";
import { SortOrder } from 'src/app/shared/modules/graphql/enums/generic.enums';
import { filter, take } from 'rxjs/operators';
import { DialogCloseResult, DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { NotificationsService } from 'src/app/shared/modules/notifications/notifications.service';
import _ from 'lodash';
import { SortDescriptor } from '@progress/kendo-data-query';
import { CompanyDeleteConfirmModalComponent } from 'src/app/components/shared/companies/company-delete-confirm-modal/company-delete-confirm-modal.component';

export interface IContactListConfig {
  hideLists?: boolean;
  companyId?: string;
  hideAddContact?: boolean;
}

@Component({
  selector: 'app-contacts-list',
  templateUrl: './contacts-list.component.html',
  styleUrls: ['./contacts-list.component.scss']
})

export class ContactsListComponent implements OnInit {

  public authConfig = {
    contactsProfileContactInformationViewAssigned,
    contactsProfileCompanyInformationView,
    contactsInvestigations,
    contactsCreateConfig,
    contactsDelete,
  };

  @Input() config: IContactListConfig = {
    hideLists: false
  };

  public search = "";
  public firstName = "";
  public lastName = "";

  public get mutateView(): boolean {
    return this.config.hideLists && this.config.hideAddContact;
  }

  // Select data
  public positions: IApiContactRole[] = [];

  public companies: IApiCompany[] = [];
  public oldFilter: any[] = [];

  public columnsToDisplay = ["name", "position", "phone", "email", "branches"];

  private _dataSource: ContactDataSource;

  public get dataSource() {
    return this._dataSource;
  }

  public set dataSource(val) {
    this._dataSource = val;
  }

  public get pageOptions() {
    if (!this._dataSource) return null;
    return this._dataSource.listPage;
  }
  selectedStatus = null;
  public contacts: IApiContact[] = [];

  public sort: SortDescriptor[] = [{
    field: 'LAST_NAME',
    dir: 'asc'
  }];

  public actionItems = [{
    text: "Delete"
  }]

  public alphabet = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(65 + i)
  );

  public nameStartWith = null;
  public countPerLetter = [];

  constructor(
    private contactService: ContactService,
    private loader: LoaderService,
    private contactRoleService: ContactRoleService,
    private dialogService: DialogService,
    private notification: NotificationsService,
    public auth: AuthService
  ) { }

  // add contact modal
  public addContactModal() {
    this.oldFilter = [...this.dataSource.lastFilters];
    const dialog: DialogRef = this.dialogService.open({
      content: CreateUpdateContactComponent,
      width: '80%',
      maxWidth: '90%',
      preventAction: (ev) => {
        return ev !== 'closed' as any;
      },
    });

    dialog.result
      .pipe(filter((v) => !_.isEmpty(v)))
      .subscribe((result: DialogCloseResult) => {
        if (this.oldFilter && this.oldFilter?.length > 0) {
          this.oldFilter.map((obj) => {
            this.dataSource.applyFilter(obj?.type, obj?.value);
          });
          this.oldFilter = [];
        }
        if (result) {
          this.updateTable(result);
        }
      });
  }

  public updateTable(a) {
    this.dataSource?.contents$?.pipe(take(1)).subscribe((res) => {
      res.pop();
      res.unshift(a);
      this.dataSource.data = res;
      this.dataSource.updateTotalCount(this.dataSource.listPage.totalCount + 1);
    })
  }

  ngOnInit() {
    this.contactRoleService.get([{ type: IApiContactRoleFilterType.ViewListView, value: 'true' }], { sortOrder: SortOrder.ASC, limit: -1 }).pipe(
      unwrapConnection()
    ).subscribe(data => this.positions = data.filter(({ isPrivate }) => !isPrivate));

    // Init table
    this._dataSource = new ContactDataSource(this.contactService);
    this.loader.attachObservable(this._dataSource.loading$);
    this.dataSource.applyFilter(IApiContactFilterType.ViewListView, "true");
    this.dataSource.applyFilter(IApiContactFilterType.IsContactActive, 'all');
    if (this.config.companyId) this.dataSource.applyFilter(IApiContactFilterType.Company, this.config.companyId);
    this.auth.hasCategoryPermission(contactsDelete.category, contactsDelete.appliedPermissions).subscribe((response) => {
      if (response === true) {
        this.columnsToDisplay.push('actions');
      }
    })
    this.dataSource?.contents$.subscribe((res) => {
      this.contacts = res;
    });
    this.load();
  }

  public getAlphabetWiseContacts() {
    this.contactService.getContactByAlphabets(this.dataSource.lastFilters).subscribe(response => {
      const parsed = JSON.parse(response);
      this.countPerLetter = this.getSortedCounts(parsed);
      this.nameStartWith = Object.keys(this.countPerLetter)?.length ? Object.keys(this.countPerLetter)[0] : null;
    });
  }

  public loadContactNameCounts() {
    if (this.sort?.length && this.sort[0]?.field === IApiContactFilterType.LastName) {
      this.getAlphabetWiseContacts();
    }
  }

  public onActivityChange(event) {
    this.dataSource.applyFilter(IApiContactFilterType.IsActive, event);
    this.load();
  }

  public onSearchChange() {
    this.dataSource.applyFilter(IApiContactFilterType.Search, this.search || null);
    this.load();
  }

  public onSearchFirstNameChange() {
    this.dataSource.applyFilter(IApiContactFilterType.FirstName, this.firstName || null);
    this.load();
  }

  public onSearchLastNameChange() {
    this.dataSource.applyFilter(IApiContactFilterType.LastName, this.lastName || null);
    this.load();
  }

  public onCompanyChange(company?: IApiCompany) {
    this.dataSource.applyFilter(IApiContactFilterType.Company, company?.id);
    this.load();
  }

  public onPositionChange(event) {
    this.dataSource.applyFilter(IApiContactFilterType.Position, event.id || null);
    this.load();
  }

  public load(reset = true): Promise<IApiContactConnection> {
    if (reset === true) {
      this.dataSource.pagingReset();
    } else if (Number.isInteger((this.dataSource.listPage?.totalCount - 1) / this.dataSource.listPage.limit)) {
      if (this.dataSource.listPage.currentPageIndex > 0) {
        this.dataSource.listPage.currentPageIndex = (this.dataSource.listPage.currentPageIndex - 1);
      } else {
        this.dataSource.pagingReset();
      }
    }
    this.loadContactNameCounts();
    return this.dataSource.load();
  }

  public removeContact(contact): void {
    this.notification.kendoConfirm(`Are you sure you want to delete this <b>${contact?.lastName ? (contact?.lastName + ', ' + contact?.firstName) : contact?.firstName}</b> contact?`, "Delete Contact")
      .subscribe((result) => {
        if (result === true) {
          this.loader.show$(this.contactService.remove({ id: contact?.id }))
            .subscribe(res => {
              if (res?.success) {
                this.load(false);
                this.notification.notify(res.message);
              } else {
                const dialog: DialogRef = this.dialogService.open({
                  content: CompanyDeleteConfirmModalComponent,
                  maxWidth: "90%",
                  preventAction: (ev) => {
                    return ev !== 'closed' as any;
                  },
                });
                const dialogInstance = dialog.content.instance as CompanyDeleteConfirmModalComponent;
                const data = res?.data ? JSON.parse(res?.data) : null;
                dialogInstance.data = { ...data, name:  `${contact?.lastName || ''} ${contact?.firstName || ''}`?.trim()};
                dialogInstance.bodyMessage = `${contact?.lastName ? (contact?.lastName + ', ' + contact?.firstName) : contact?.firstName} is attached to one or more investigation as Scene contact, Bill to, Client contact or Investigation Parties for below case records.`;
                dialog.result
                  .subscribe((response) => {
                  });
              }
            }, (err) => {
              this.notification.error("Failed to delete contact");
            });
        }
      })
  }

  public pageChange(event) {
    this.nameStartWith = this.getCurrentAlphabetForPage(event.skip, event.take)
    this.pageOptions?.paginate(event)
  }

  public sortChange = (e: SortDescriptor[]) => {
    this.sort = e;
    if (e && e?.[0]?.dir) {
      this.dataSource.listPage.orderBy = e?.[0]?.field;
      this.dataSource.listPage.sortOrder = e?.[0]?.dir === 'asc' ? SortOrder.ASC : SortOrder.DESC;
    } else {
      this.dataSource.listPage.orderBy = 'LAST_NAME';
      this.dataSource.listPage.sortOrder = SortOrder.DESC;
    }
    this.load();
  }

  public nameStartWithChange(char) {
    this.nameStartWith = char;
    let pageNumber = this.getPageForChar(this.nameStartWith);
    if (!pageNumber)
      this.nameStartWith = '#';

    this.dataSource.listPage.currentPageIndex = pageNumber || 0;
    this.dataSource.listPage.previousPageIndex = pageNumber - 1;
    window.scrollTo(0, 0);
    this.dataSource.listPage.paginate$.next();
  }

  private getSortedCounts(data) {
    const sorted = [...data]?.sort((a, b) => {
      const aKey = a?.i === '#' ? ' ' : a?.i;  // put '#' first (before 'A')
      const bKey = b?.i === '#' ? ' ' : b?.i;
      return aKey.localeCompare(bKey);
    });

    return Object.fromEntries(sorted?.map(({ i, c }) => [i, c]));
  }

  public getPageForChar(char: string): number | null {
    const pageStartMap: Record<string, number> = {};
    let offset = 0;
    const limit = this.dataSource.listPage.limit;
    for (const letter of Object.keys(this.countPerLetter)) {
      const count = this.countPerLetter[letter];
      pageStartMap[letter] = Math.floor(offset / limit);
      offset += count;
    }
    const key = (char || '').toUpperCase();
    return pageStartMap.hasOwnProperty(key) ? pageStartMap[key] : null;
  }

  private getCurrentAlphabetForPage(skip: number, take: number): string | null {
    let runningStart = 0;
    const pageStart = skip;
    const pageEnd = skip + take - 1;

    for (const letter of Object.keys(this.countPerLetter)) {
      const count = this.countPerLetter[letter];
      const runningEnd = runningStart + count - 1;
      if (!(pageEnd < runningStart || pageStart > runningEnd)) {
        return letter;
      }
      runningStart += count;
    }
    return null;
  }


}
